import { errorMessage } from "@streamoid/errors";
import React, { useEffect, useMemo, useState } from "react";
import { notify } from "../../utils/utils";
import {
  getWorkspaceOrgMigrationPlan,
  migrateWorkspacesByDomain,
} from "../../utils/organizations";
import "./style.css";

interface PlanWorkspace {
  workspaceId: string;
  workspaceName: string;
  status: string;
  ownerUserId: string;
  ownerEmail: string;
  ownerName: string;
  adminCount: number;
  otherAdminDomains: string[];
}

interface PlanGroup {
  domain: string;
  plannedOrgName: string;
  action: "create" | "join-existing";
  existingOrganizationId: string | null;
  existingOrganizationName: string | null;
  owner: { userId: string; email: string; name: string } | null;
  workspaces: PlanWorkspace[];
}

interface PlanSkip {
  workspaceId: string;
  workspaceName: string;
  ownerEmail: string;
  emailDomain: string;
  reason: "already-in-org" | "public-domain" | "no-owner";
}

interface MigrationPlan {
  groups: PlanGroup[];
  skipped: PlanSkip[];
  summary: {
    workspaces: number;
    groups: number;
    toCreate: number;
    toJoin: number;
    skipped: number;
  };
}

const SKIP_LABEL: Record<PlanSkip["reason"], string> = {
  "already-in-org": "Already in an organization",
  "public-domain": "Public / personal email domain",
  "no-owner": "No owner / email",
};

export default function WorkspaceOrgMigration() {
  const [plan, setPlan] = useState<MigrationPlan | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // domain -> Set of selected workspaceIds
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  // domain currently being migrated (disables its button)
  const [migratingDomain, setMigratingDomain] = useState<string | null>(null);
  const [showSkipped, setShowSkipped] = useState(false);

  const fetchPlan = () => {
    setIsLoading(true);
    getWorkspaceOrgMigrationPlan()
      .then((res) => {
        const data: MigrationPlan = res?.data || {
          groups: [],
          skipped: [],
          summary: {
            workspaces: 0,
            groups: 0,
            toCreate: 0,
            toJoin: 0,
            skipped: 0,
          },
        };
        setPlan(data);
        // Default: every workspace in every group is selected.
        const initial: Record<string, Set<string>> = {};
        for (const g of data.groups) {
          initial[g.domain] = new Set(g.workspaces.map((w) => w.workspaceId));
        }
        setSelected(initial);
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchPlan();
  }, []);

  const toggleWorkspace = (domain: string, workspaceId: string) => {
    setSelected((prev) => {
      const next = { ...prev };
      const set = new Set(next[domain] || []);
      if (set.has(workspaceId)) set.delete(workspaceId);
      else set.add(workspaceId);
      next[domain] = set;
      return next;
    });
  };

  const toggleGroup = (group: PlanGroup, checked: boolean) => {
    setSelected((prev) => ({
      ...prev,
      [group.domain]: checked
        ? new Set(group.workspaces.map((w) => w.workspaceId))
        : new Set(),
    }));
  };

  const handleMigrate = (group: PlanGroup) => {
    const workspaceIds = [...(selected[group.domain] || [])];
    if (!workspaceIds.length) {
      notify("Select at least one workspace to migrate", "error");
      return;
    }

    const verb =
      group.action === "create"
        ? `create organization "${group.plannedOrgName}" and move`
        : `move`;
    const ok = window.confirm(
      `This will ${verb} ${workspaceIds.length} workspace(s) for ${group.domain}` +
        (group.action === "join-existing"
          ? ` into "${group.existingOrganizationName}".`
          : `, claim the domain, and make ${
              group.owner?.email || "the owner"
            } an org admin.`),
    );
    if (!ok) return;

    setMigratingDomain(group.domain);
    migrateWorkspacesByDomain({ domain: group.domain, workspaceIds })
      .then((res) => {
        const moved = res?.data?.movedCount ?? workspaceIds.length;
        notify(`Migrated ${moved} workspace(s) for ${group.domain}`);
        fetchPlan();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => setMigratingDomain(null));
  };

  const summary = plan?.summary;
  const groups = plan?.groups || [];
  const skipped = plan?.skipped || [];

  const selectedCountByDomain = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [domain, set] of Object.entries(selected)) out[domain] = set.size;
    return out;
  }, [selected]);

  return (
    <div className="ws-org-migration-page">
      <div className="dashboard-settings-section">
        <div className="section-heading">Organization migration</div>

        <div className="settings-container">
          <div className="version-header">
            <div className="version-title">Workspaces grouped by owner domain</div>
            <div className="version-subtitle">
              {summary
                ? `${summary.workspaces} workspaces · ${summary.toCreate} new org(s) to create · ${summary.toJoin} to join existing · ${summary.skipped} skipped`
                : "Review each domain group, then migrate its workspaces into an organization."}
            </div>
          </div>
          <div className="version-controls">
            <button
              className="version-save-btn"
              onClick={fetchPlan}
              disabled={isLoading || migratingDomain !== null}
            >
              {isLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="settings-container">
            <div className="version-subtitle">Loading...</div>
          </div>
        ) : groups.length === 0 ? (
          <div className="settings-container">
            <div className="version-subtitle">
              No workspaces are eligible for migration.
            </div>
          </div>
        ) : (
          groups.map((group) => {
            const selCount = selectedCountByDomain[group.domain] || 0;
            const allSelected = selCount === group.workspaces.length;
            const isMigrating = migratingDomain === group.domain;
            return (
              <div
                className="settings-container ws-org-migration-group"
                key={group.domain}
              >
                <div className="ws-org-migration-group-header">
                  <div>
                    <div className="version-title">{group.domain}</div>
                    <div className="version-subtitle">
                      {group.action === "create" ? (
                        <>
                          Create org <strong>{group.plannedOrgName}</strong> ·
                          owner {group.owner?.email || "-"} becomes admin
                        </>
                      ) : (
                        <>
                          Join existing org{" "}
                          <strong>{group.existingOrganizationName}</strong>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    className="version-save-btn"
                    onClick={() => handleMigrate(group)}
                    disabled={isMigrating || selCount === 0}
                  >
                    {isMigrating
                      ? "Migrating..."
                      : `Migrate ${selCount} workspace(s)`}
                  </button>
                </div>

                <div className="organizations-table-scroll">
                  <table className="organizations-table">
                    <thead>
                      <tr>
                        <th className="ws-org-migration-check-col">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={(e) => toggleGroup(group, e.target.checked)}
                            aria-label={`Select all workspaces for ${group.domain}`}
                          />
                        </th>
                        <th>Workspace</th>
                        <th>Owner</th>
                        <th>Status</th>
                        <th>Other admin domains</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.workspaces.map((ws) => (
                        <tr key={ws.workspaceId}>
                          <td className="ws-org-migration-check-col">
                            <input
                              type="checkbox"
                              checked={
                                selected[group.domain]?.has(ws.workspaceId) ||
                                false
                              }
                              onChange={() =>
                                toggleWorkspace(group.domain, ws.workspaceId)
                              }
                              aria-label={`Select ${ws.workspaceName}`}
                            />
                          </td>
                          <td>{ws.workspaceName || "-"}</td>
                          <td>{ws.ownerEmail || "-"}</td>
                          <td>
                            <span
                              className={`organizations-status-badge organizations-status-${
                                ws.status || "active"
                              }`}
                            >
                              {ws.status || "active"}
                            </span>
                          </td>
                          <td>
                            {ws.otherAdminDomains?.length ? (
                              <span className="ws-org-migration-warn">
                                {ws.otherAdminDomains.join(", ")}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}

        {skipped.length > 0 && (
          <div className="settings-container">
            <div
              className="ws-org-migration-skipped-toggle"
              onClick={() => setShowSkipped((s) => !s)}
            >
              <div className="version-title">
                Skipped ({skipped.length}) {showSkipped ? "▲" : "▼"}
              </div>
              <div className="version-subtitle">
                Workspaces not eligible for migration
              </div>
            </div>
            {showSkipped && (
              <div className="organizations-table-scroll">
                <table className="organizations-table">
                  <thead>
                    <tr>
                      <th>Workspace</th>
                      <th>Owner</th>
                      <th>Domain</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skipped.map((s) => (
                      <tr key={s.workspaceId}>
                        <td>{s.workspaceName || "-"}</td>
                        <td>{s.ownerEmail || "-"}</td>
                        <td>{s.emailDomain || "-"}</td>
                        <td>{SKIP_LABEL[s.reason] || s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
